import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // On initial load, align the gallery to the current variant's featured media (if any).
    // - grid: scroll the document to the media
    // - carousel: swipe/select the matching slide
    this.#scrollToInitialVariantMedia();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #scrollToInitialVariantMedia() {
    const mediaId = this.dataset.initialVariantMediaId;
    if (!mediaId) return;

    // Wait a frame so slideshow/grid layout is ready.
    requestAnimationFrame(() => {
      const escapedMediaId = CSS.escape(mediaId);
      const isMobile = isMobileBreakpoint();
      const isGridMode = this.presentation === 'grid';
      const isCarouselMode = this.presentation === 'carousel';

      // Determine which component is actually visible:
      // - Carousel mode: always use slideshow
      // - Grid mode on mobile: use slideshow (CSS shows it as carousel)
      // - Grid mode on desktop: use grid
      const shouldUseSlideshow = isCarouselMode || (isGridMode && isMobile);

      if (shouldUseSlideshow && this.slideshow) {
        // For carousel (or grid on mobile): find slideshow-slide and select it
        const slide = this.querySelector(`slideshow-slide[data-media-id="${escapedMediaId}"]`);
        if (!slide || !(slide instanceof HTMLElement)) return;

        const slides = this.slideshow.slides;
        if (!slides?.length) return;
        // Cast to HTMLElement for indexOf compatibility
        const index = slides.indexOf(/** @type {HTMLElement} */ (slide));
        if (index < 0) return;
        this.slideshow.select(index, undefined, { animate: true });
        return;
      }

      // For grid on desktop: find the <li> element with data-media-id and scroll to it
      const gridItem = this.querySelector(`li[data-media-id="${escapedMediaId}"]`);
      if (!gridItem) return;

      // Scroll the grid item into view
      gridItem.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
